import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { from } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { RecordSearchComponent } from '../components';
import {
  GetCallRecordVm,
  ListCampaignSearchCriteriaVm,
  RecordDto,
  SearchCallRecordsQuery,
  SearchCallRecordsVm,
} from '../models';
import { StartSearchOptions } from '../code';
import { shareReplayRefCount } from '@adreno-common';

@Injectable({
  providedIn: 'root',
})
export class RecordService {
  private recordsApi = `${environment.apiPath}/records`;

  constructor(private modalSvc: NgbModal, private http: HttpClient) {}

  private getModal() {
    return this.modalSvc.open(RecordSearchComponent, {
      size: 'xl',
      backdrop: 'static',
      keyboard: false,
    });
  }

  private modalResult$(modalRef: NgbModalRef) {
    return from(modalRef.result).pipe(
      map((result) => result as RecordSearchResults)
    );
  }

  getNewRecord$(campaignId: number) {
    return this.http.get<number>(`${this.recordsApi}/${campaignId}`);
  }

  getRecord$(campaignId: number, recordId: number) {
    return this.http
      .get<GetCallRecordVm>(`${this.recordsApi}/${campaignId}/${recordId}`)
      .pipe(
        map((vm) => ({
          ...vm.record,
          master: {
            ...vm.record.master,
            u2: vm.record.master.u2
              ? new Date(vm.record.master.u2 as unknown as string) //convert json string to actual Date object in local browser timezone (should be missing Z suffix)
              : undefined,
          },
        }))
      );
  }

  listCampaignSearchCriteria(campaignId: number) {
    return this.http
      .get<ListCampaignSearchCriteriaVm>(
        `${this.recordsApi}/${campaignId}/searchcriteria`
      )
      .pipe(
        map((vm) => vm.criteria),
        shareReplayRefCount()
      );
  }

  searchRecords$(requestdata: SearchCallRecordsQuery) {
    const params = (
      Object.keys(requestdata) as Array<keyof SearchCallRecordsQuery>
    ).reduce((acc, key) => {
      if (requestdata[key] === undefined) return { ...acc };
      if (key == 'u2')
        return { ...acc, [key]: requestdata[key]?.toJSON().slice(0, 10) };
      return { ...acc, [key]: requestdata[key] };
    }, {});

    return this.http
      .get<SearchCallRecordsVm>(`${this.recordsApi}/search`, {
        params,
      })
      .pipe(
        map((vm) => vm.records),
        shareReplayRefCount()
      );
  }

  startSearch$(terms: SearchCallRecordsQuery, options: StartSearchOptions) {
    const modalRef = this.getModal();
    modalRef.componentInstance.terms = terms;
    modalRef.componentInstance.options = options;
    return this.modalResult$(modalRef);
  }
}

export interface CancelRecordSearchResult {
  type: 'cancel';
}

export interface NewRecordSearchResult {
  recordId: number;
  type: 'new';
}

export interface ExistingRecordSearchResult {
  record: RecordDto;
  type: 'existing';
}

export type RecordSearchResults =
  | CancelRecordSearchResult
  | NewRecordSearchResult
  | ExistingRecordSearchResult;
